import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import PageHeader from '../PageHeader';

import { api } from '../../../lib/api';
import { SITE_URL } from '../../../lib/seo';

export default function BlogList() {
  const { t } = useTranslation();
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .listArticles()
      .then((data) => {
        if (active) setArticles(data.articles || []);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <Box>
      <Helmet>
        <title>Blog | Al-Amen Technology</title>
        <meta name="description" content="Product updates, field notes and event announcements from the Al-Amen Technology team." />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
      </Helmet>

      <PageHeader eyebrow={t('blogHub.eyebrow')} title={t('blogHub.title')} body={t('blogHub.intro')} />

      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 } }}>
        {error || (articles && articles.length === 0) ? (
          <Typography sx={{ color: 'text.secondary' }}>{t('blogHub.empty')}</Typography>
        ) : null}

        {articles && articles.length > 0 ? (
          <Grid container spacing={3}>
            {articles.map((article) => (
              <Grid key={article.id} item xs={12} md={4}>
                <Paper
                  component={RouterLink}
                  to={`/blog/${article.slug}`}
                  variant="outlined"
                  sx={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                    p: { xs: 3, md: 3.5 },
                    borderRadius: 3,
                    height: '100%',
                    transition: 'transform 200ms ease, border-color 200ms ease',
                    '&:hover': { transform: 'translateY(-4px)', borderColor: 'primary.main' }
                  }}
                >
                  {article.coverImageUrl ? (
                    <Box
                      component="img"
                      src={article.coverImageUrl}
                      alt={article.coverImageAlt || ''}
                      loading="lazy"
                      sx={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 2, mb: 2 }}
                    />
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
                    {article.category ? <Chip label={article.category} size="small" variant="outlined" /> : null}
                    {article.isFestival ? <Chip label="Event" size="small" color="primary" /> : null}
                  </Stack>
                  <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.6rem', lineHeight: 1.15, letterSpacing: '-0.015em', mb: 1.5 }}>
                    {article.title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', mb: 2 }}>{article.summary}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('blogHub.publishedOn')}{' '}
                    {new Date(article.publishedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Container>
    </Box>
  );
}
